import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { HERO_STATS, CHAT_SCRIPT } from "@/data";
import { Lines } from "@/components/primitives";
import { DemoLink } from "@/components/Booking";
import { TrialLink, TRIAL_NOTE } from "@/components/marketing/Shared";

const useClock = () => {
  const [t, setT] = useState("");
  useEffect(() => {
    const tick = () => setT(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    tick();
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, []);
  return t;
};

const ChatSim = () => {
  const [n, setN] = useState(0);
  const [typing, setTyping] = useState(false);
  const logRef = useRef(null);
  useEffect(() => {
    let alive = true;
    let timer;
    const schedule = (fn, ms) => { timer = setTimeout(() => alive && fn(), ms); };
    const advance = (count) => {
      if (count >= CHAT_SCRIPT.length) return schedule(() => { setN(0); schedule(() => advance(0), 900); }, 5000);
      const next = CHAT_SCRIPT[count];
      if (next.who === "agent") {
        setTyping(true);
        return schedule(() => { setTyping(false); setN(count + 1); schedule(() => advance(count + 1), 2600); }, 1300);
      }
      setN(count + 1);
      schedule(() => advance(count + 1), next.who === "system" ? 1800 : 2200);
    };
    schedule(() => advance(0), 900);
    return () => { alive = false; clearTimeout(timer); };
  }, []);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [n, typing]);
  const shown = CHAT_SCRIPT.slice(0, n);
  const resolved = n >= CHAT_SCRIPT.length;
  return (
    <div className="glass rounded-3xl p-5 relative overflow-hidden" data-testid="hero-chat-sim">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[11px] text-[var(--qd-accent)]">Nova AI · order #48213</span>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 transition-colors ${resolved ? "text-emerald-700 bg-emerald-50" : "text-[var(--qd-accent)] bg-[var(--qd-tint)]"}`} data-testid="hero-chat-status">
          <span className="live-dot" /> {resolved ? "Resolved · $0.79" : "Resolving"}
        </span>
      </div>
      <div ref={logRef} className="space-y-3 h-[610px] sm:h-[500px] overflow-hidden" aria-live="polite" data-testid="hero-chat-log">
        {shown.map((m, i) => (
          <motion.div key={`${m.who}-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} data-testid={`hero-chat-message-${i}`}>
            {m.who === "system" ? (
              <p className="font-mono text-[11px] text-slate-500 pl-1">{m.text}</p>
            ) : (
              <div className={`flex ${m.who === "agent" ? "justify-end" : ""}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.who === "agent" ? "bg-[var(--qd-accent)] text-white rounded-br-md" : "bg-white text-[var(--qd-ink)] rounded-bl-md border border-[var(--qd-line)]"}`}>
                  <span className={`block font-mono text-[9px] tracking-[0.2em] mb-1 ${m.who === "agent" ? "text-white/60" : "text-slate-400"}`}>{m.who === "agent" ? "NOVA AI" : "CUSTOMER"}</span>
                  {m.text}
                </div>
              </div>
            )}
          </motion.div>
        ))}
        {typing && (
          <div className="flex justify-end">
            <div className="typing flex gap-1 rounded-2xl bg-[var(--qd-tint)] px-3 py-2.5">
              <span /> <span /> <span />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const Hero = () => {
  const clock = useClock();
  return (
    <section id="top" className="relative pt-36 pb-24 lg:pt-44 lg:pb-32" data-testid="hero">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
        <div className="lg:col-span-7">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 font-mono text-[11px] tracking-[0.18em] text-[var(--qd-accent)] uppercase" data-testid="hero-live-badge">
            <span className="live-dot" /> Agents live {clock}
          </motion.span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.6rem] leading-[0.98] mt-7 text-[var(--qd-ink)]" data-testid="hero-title">
            <Lines lines={[<em className="italic font-normal" data-testid="hero-agentic">Go <span className="text-[var(--qd-accent)]" data-testid="hero-agentic-word">Agentic</span></em>, <span className="block text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3" data-testid="hero-subtitle">Resolve tickets at <span className="text-[var(--qd-accent)] font-bold">$0.79</span></span>]} delay={0.25} />
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-4 text-base text-[var(--qd-muted)]" data-testid="hero-seat-note">No seat costs.</motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-8">
            <TrialLink className="btn-primary" testId="hero-try-free">Try free for two months <ArrowRight className="w-4 h-4" /></TrialLink>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }} className="mt-4 max-w-md text-xs leading-relaxed text-[var(--qd-muted)]" data-testid="hero-trial-terms">{TRIAL_NOTE} $0.79 per prepaid resolution afterward.</motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="mt-10 grid grid-cols-3 gap-6 max-w-lg" data-testid="hero-stats">
            {HERO_STATS.map((s, i) => (
              <div key={s.label} className="border-l border-[var(--qd-line)] pl-4">
                <p className="font-display text-3xl sm:text-4xl text-[var(--qd-ink)]" data-testid={`hero-stat-value-${i}`}>{s.value}</p>
                <p className="text-xs text-[var(--qd-muted)] mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }} className="mt-12 max-w-xl">
            <ChatSim />
          </motion.div>
        </div>

        <motion.aside initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6, duration: 1, ease: [0.2, 0.8, 0.2, 1] }} className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="relative rounded-3xl p-7 sm:p-8 text-white overflow-hidden grain" style={{ background: "linear-gradient(160deg, #5000ff 0%, #3b0fd4 55%, #1f0f66 100%)" }} data-testid="hero-demo-card">
            <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute right-8 bottom-10 w-24 h-24 rounded-full border border-white/20 orbit-ring">
              <span className="absolute -top-1.5 left-1/2 w-3 h-3 rounded-full bg-white shadow-[0_0_20px_white]" />
            </div>
            <p className="eyebrow !text-white/70 mb-4 relative">Free for two months</p>
            <h2 className="font-display text-2xl sm:text-3xl leading-tight relative" data-testid="hero-card-title">Start resolving at $0.79. No seats to buy.</h2>
            <p className="mt-3 text-sm text-white/70 relative" data-testid="hero-card-description">{TRIAL_NOTE} Afterwards, prebuy resolutions at $0.79 each; unused credits carry forward.</p>
            <div className="mt-7 relative">
              <DemoLink ctx={{ source: "hero" }} className="btn-primary w-full justify-center !bg-white !text-[var(--qd-accent)]" testId="hero-book-demo" />
              <p className="mt-4 text-xs leading-relaxed text-white/50">Pick a slot that suits you. No credit card, no commitment.</p>
            </div>
          </div>
        </motion.aside>
      </div>
    </section>
  );
};
